'use client';

// ВХОД ШТОРКОЙ В БРАУЗЕРЕ (ТЗ WEB-LOOK Э1, владелец 30.09.2026: «вход — не отдельные пустые
// страницы с одним инпутом; на мобильном снизу выезжает шторка, на десктопе тоже что-то
// выезжает»). Хост даёт публичным страницам (лендинг, /login, /register, /verify-phone,
// /welcome) ту же шторку, что у кабинета: DrawerProvider — на телефоне лист снизу, на
// ≥821 панель 460 справа с затемнением. Под листом — подложка страницы: на лендинге сам
// лендинг, на страницах входа — приветствие с шарами (AppOnboarding backdrop).
//
// Класс `cab` — ради правил `.cab .drawer*` и переменных кабинета; `cab-auth` гасит
// лишнее (login.css: display: contents, без пятен фона, слои над приветствием).
//
// INVARIANT(scriptera): хост монтируется ТОЛЬКО в браузере. В iOS-приложении (UA
// ScripteraApp) страницы входа рисуют прежнюю разметку — решает сервер по UA в page.tsx;
// NativeLayerHost здесь не монтируется. Причина: вход в приложении ведут родные экраны
// (NativeAuthView), а веб в шторке приложения обязан остаться ровно прежним (правило 1.6).
// Канон: ~/projects/WEB-LOOK-TZ.md, Э1.
import { useCallback, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { KBareButton } from '@scriptera/ui/kit';
import { useRouter } from '@/i18n/routing';
import { DrawerProvider, useDrawer, type DrawerPayload } from '@/components/cabinet/drawer';
import { LoginForm } from '@/components/auth/login-form';
import { VerifyPhone } from '@/components/pay/verify-phone';
import { WelcomeFlow } from '@/components/onboarding/welcome-flow';
import { AUTH_EVENT, type AuthEvent } from './auth-events';

export type AuthScreen = 'login' | 'register' | 'phone' | 'welcome';

/** Что знает сервер про экран «после телефона» (прямой заход на /welcome). */
export interface WelcomeProps {
  has: { tg: boolean; ig: boolean };
  startAtNiche: boolean;
}

export function AuthHost({
  screen,
  welcome,
  children,
}: {
  screen: AuthScreen | null;
  welcome?: WelcomeProps;
  children: React.ReactNode;
}) {
  return (
    <div className="cab cab-auth">
      <DrawerProvider>
        {children}
        <AuthSheetDriver screen={screen} welcome={welcome} />
      </DrawerProvider>
    </div>
  );
}

/** Текущий адрес строки (путь + параметры): лист прямого захода кладётся на ЭТОТ адрес,
 *  чтобы открытие не дописало запись истории и не потеряло `?next=` / `?deleted=`. */
function here(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** Пустой левый слот шапки: у листа телефона закрытия нет (кабинет под гейтом телефона). */
const NO_LEAD = <span className="dr-act hidden" aria-hidden="true" />;
/** Уход с экрана запрещён: «назад» (Escape, клик мимо, свайп) корня с winKey зовёт onBack. */
const STAY = () => {};

/**
 * Водитель листов входа — без разметки. Корень — вход/регистрация; после входа тот же
 * корень становится листом телефона, затем «добавьте аккаунт» (drawer.update — подмена на
 * месте, а не стопка: назад к форме пути нет, сессия уже выдана). Решения Fable 30.09:
 *  • телефон ЗАКРЫТЬ НЕЛЬЗЯ, как в iOS: крестика нет, Escape/клик мимо/свайп — no-op
 *    (корень с winKey + onBack-заглушкой), внутри остаётся тихий «Выйти»;
 *  • «добавьте аккаунт»: крестик/Escape/клик мимо/свайп = «Позже» — в «Боты».
 * Так подложка без кнопок никогда не остаётся одна. Адреса — с языком (ТЗ Э0.4 п.7).
 */
function AuthSheetDriver({ screen, welcome }: { screen: AuthScreen | null; welcome?: WelcomeProps }) {
  const drawer = useDrawer();
  const locale = useLocale();
  const router = useRouter();
  const tc = useTranslations('cabinet');
  const landing = `/${locale}`;
  // Открытия зовут друг друга (вход → телефон → аккаунт) — держим их в ref, чтобы
  // замыкания тел листов не протухали.
  const api = useRef<{
    root: (kind: AuthScreen, direct: boolean) => void;
  } | null>(null);

  // Конец онбординга: в «Боты» кабинета (клиентская навигация + свежие серверные данные).
  const goBots = useCallback(() => {
    router.replace('/app/bots');
    router.refresh();
  }, [router]);

  // «Позже» с экрана «добавьте аккаунт» (крестик, Escape, клик мимо, свайп): шаг
  // закрывается на сервере, как кнопкой «Позже» (POST /api/user/welcome), — иначе гейт
  // кабинета вернул бы человека на /welcome.
  const later = useCallback(() => {
    void fetch('/api/user/welcome', { method: 'POST' })
      .catch(() => {})
      .finally(goBots);
  }, [goBots]);

  const welcomePayload = useCallback(
    (w: WelcomeProps): Omit<DrawerPayload, 'href' | 'closeHref'> => ({
      title: '',
      sheet: true,
      winKey: 'auth-welcome',
      // Уйти с экрана = «Позже»: тот же ход, что кнопка.
      onBack: later,
      headerLeft: (
        <KBareButton className="dr-close" onClick={later} aria-label={tc('close')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </KBareButton>
      ),
      body: <WelcomeFlow locale={locale} has={w.has} startAtNiche={w.startAtNiche} inSheet onDone={goBots} />,
    }),
    [locale, goBots, later, tc],
  );

  /** Подменить открытый корень следующим шагом и поставить его адрес (Cmd+R даёт тот же
   *  шаг). Запись истории — без ключа уровня: «назад» шапки не уходит в историю, а
   *  упирается в onBack шага. */
  const step = useCallback(
    (p: Omit<DrawerPayload, 'href' | 'closeHref'>, url: string) => {
      drawer.update({ ...p, href: undefined, closeHref: undefined });
      window.history.replaceState(null, '', url);
    },
    [drawer],
  );

  // Телефон подтверждён: спрашиваем сервер, нужен ли экран «добавьте аккаунт».
  const afterPhone = useCallback(() => {
    void fetch('/api/user/welcome-state', { cache: 'no-store' })
      .then((r) => r.json())
      .then((j: { ok?: boolean; show?: boolean } & Partial<WelcomeProps>) => {
        if (j?.ok && j.show && j.has) {
          step(welcomePayload({ has: j.has, startAtNiche: !!j.startAtNiche }), `/${locale}/welcome`);
        } else goBots();
      })
      .catch(() => goBots());
  }, [step, welcomePayload, locale, goBots]);

  const phonePayload = useCallback(
    (): Omit<DrawerPayload, 'href' | 'closeHref'> => ({
      title: '',
      sheet: true,
      winKey: 'auth-phone',
      onBack: STAY,
      headerLeft: NO_LEAD,
      body: <VerifyPhone locale={locale} inSheet onVerified={afterPhone} />,
    }),
    [locale, afterPhone],
  );

  const root = useCallback(
    (kind: AuthScreen, direct: boolean) => {
      // Прямой заход — лист на текущем адресе (записи истории нет); закрытие входа
      // возвращает адрес лендинга. Открытие кнопкой — своя запись истории.
      const href = direct ? here() : `/${locale}/${kind}`;
      if (kind === 'login' || kind === 'register') {
        drawer.open({
          // СМЕНА РЕЖИМА ЖИВОГО КОРНЯ (вход ↔ регистрация, заход по адресу при открытом листе) —
          // тот же лист с новым содержимым, не уровень поверх (ТЗ «Причины» П1: подмена по флагу).
          replace: true,
          title: '',
          sheet: true,
          // Лист-стопка на телефоне (ТЗ WEB-PARITY-3 В11, кадр iOS A06-register): страница под
          // листом уходит назад колодой, её кнопки не торчат над листом. Крестика у листа нет
          // (владелец 03.10.2026, кадр A11-login-email: у родного NativeAuthView только ручка —
          // закрытие смахиванием и тапом мимо).
          ...(typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches
            ? { headerLeft: NO_LEAD }
            : {}),
          href,
          closeHref: landing,
          body: (
            <LoginForm
              key={kind}
              mode={kind}
              inSheet
              onSwitchMode={(m) => api.current?.root(m, false)}
              onNext={() => step(phonePayload(), `/${locale}/verify-phone`)}
            />
          ),
        });
        return;
      }
      // Телефон и «добавьте аккаунт» открываются корнем только прямым заходом.
      if (kind === 'phone') {
        drawer.open({ ...phonePayload(), href: here(), replace: true });
        return;
      }
      if (welcome) drawer.open({ ...welcomePayload(welcome), href: here(), replace: true });
    },
    [drawer, locale, landing, step, phonePayload, welcomePayload, welcome],
  );
  api.current = { root };

  // Экран страницы — лист на монтировании. drawer.open зовёт flushSync: из эффекта только
  // отложенно, иначе React падает «flushSync was called from inside a lifecycle method».
  // Экран сменился на «никакого» — лист уходит (на первом монтировании закрывать нечего).
  const prevScreen = useRef<AuthScreen | null>(null);
  useEffect(() => {
    const was = prevScreen.current;
    prevScreen.current = screen;
    const id = window.setTimeout(() => {
      if (screen) api.current?.root(screen, true);
      else if (was) drawer.close();
    }, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Кнопки подложки (приветствие, лендинг): открыть лист / уйти в мини-тур.
  useEffect(() => {
    function onAuth(e: Event) {
      const kind = (e as CustomEvent<AuthEvent>).detail;
      if (kind === 'tour') {
        drawer.close();
        return;
      }
      if (kind === 'login' || kind === 'register') api.current?.root(kind, false);
    }
    window.addEventListener(AUTH_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_EVENT, onAuth);
  }, [drawer]);

  return null;
}
